import React from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import IIMCLogo from './IIMCLogo';
import { useState } from 'react';

const FloatingIIMCLogo: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isDark = theme.palette.mode === 'dark';
  const [showBubble, setShowBubble] = useState(false);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: isMobile ? 16 : 32,
        top: isMobile ? 100 : 120, // Position below header
        zIndex: 1000,
        pointerEvents: 'auto', // Make it clickable
      }}
    >
      {/* IIMC Logo with Link */}
      <a 
        href="https://medium.com/@priyakantcharokar/iimc-smp-batch-23-revision-notes-6ff59af63893"
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
        onMouseEnter={() => setShowBubble(true)}
        onMouseLeave={() => setShowBubble(false)}
        onFocus={() => setShowBubble(true)}
        onBlur={() => setShowBubble(false)}
        onTouchStart={() => setShowBubble(v => !v)}
      >
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 2, ease: 'easeOut' }}
          style={{ position: 'relative' }}
        >
          {/* Animated Speech Bubble */}
          {showBubble && (
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              whileHover={{ scale: 1.04, boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)' }}
              style={{
                position: 'absolute',
                left: isMobile ? 0 : 70,
                top: isMobile ? -56 : 0,
                zIndex: 2000,
                minWidth: isMobile ? 80 : 120,
                maxWidth: isLandscape ? 320 : (isMobile ? 240 : 340),
                padding: isLandscape ? '12px 24px' : (isMobile ? '8px 12px' : '10px 16px'),
                fontSize: isLandscape ? '1rem' : (isMobile ? '0.82rem' : '0.92rem'),
                fontFamily: 'Allura, cursive',
                fontWeight: 400,
                color: '#222',
                background: '#fffbe6',
                borderRadius: 6,
                boxShadow: '0 6px 32px 0 rgba(255, 193, 7, 0.18)',
                border: 'none',
                display: 'block',
                pointerEvents: 'auto',
                transition: 'all 0.2s',
                whiteSpace: 'normal',
                overflow: 'visible',
                animation: 'calloutPulse 1.2s infinite',
                transform: isMobile ? 'rotate(-2deg)' : 'translateY(0) rotate(-3deg)',
                marginLeft: isMobile ? 0 : 12,
                marginTop: isMobile ? 0 : 0,
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}
            >
              <div style={{
                textAlign: 'center',
                fontWeight: 700,
                fontSize: isLandscape ? '1.08rem' : (isMobile ? '0.92rem' : '1rem'),
                marginBottom: 2,
                letterSpacing: '0.01em',
              }}>
                IIMc SMP
              </div>
              <div style={{
                textAlign: 'center',
                fontWeight: 400,
                fontSize: isLandscape ? '0.98rem' : (isMobile ? '0.82rem' : '0.92rem'),
                color: '#444',
                lineHeight: 1.3,
                letterSpacing: '0.01em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                Notes Hub! 📚✨
              </div>
            </motion.div>
          )}
          <Box
            sx={{
              position: 'relative',
              '&:hover': {
                '& .floating-logo': {
                  transform: 'scale(1.1) translateY(-5px)',
                  filter: isDark ? 'brightness(1.2) contrast(1.2)' : 'brightness(1.1) contrast(1.1)',
                },
              },
            }}
          >
            <Box
              className="floating-logo"
              sx={{
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: isDark ? 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))' : 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                '& img': {
                  filter: isDark ? 'brightness(1.1) contrast(1.1)' : 'brightness(1) contrast(1.05)',
                },
              }}
            >
              <IIMCLogo size="large" showAlumni={true} alt="Know More" />
            </Box>
            {/* Subtle glow effect */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? 60 : 80,
                height: isMobile ? 60 : 80,
                borderRadius: '50%',
                background: isDark 
                  ? 'radial-gradient(circle, rgba(120, 119, 198, 0.15) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(120, 119, 198, 0.1) 0%, transparent 70%)',
                zIndex: -1,
                animation: 'pulse 3s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': {
                    opacity: 0.6,
                    transform: 'translate(-50%, -50%) scale(1)',
                  },
                  '50%': {
                    opacity: 0.3,
                    transform: 'translate(-50%, -50%) scale(1.1)',
                  },
                },
              }}
            />
          </Box>
        </motion.div>
      </a>
    </Box>
  );
};

export default FloatingIIMCLogo; 

// Cartoon-style speech bubble animation
const style = document.createElement('style');
style.innerHTML = `
@keyframes fidoWobble {
  0% { transform: rotate(-2deg) scale(1.02); }
  10% { transform: rotate(2deg) scale(1.04); }
  20% { transform: rotate(-2deg) scale(1.06); }
  30% { transform: rotate(2deg) scale(1.08); }
  40% { transform: rotate(-2deg) scale(1.1); }
  50% { transform: rotate(2deg) scale(1.08); }
  60% { transform: rotate(-2deg) scale(1.06); }
  70% { transform: rotate(2deg) scale(1.04); }
  80% { transform: rotate(-2deg) scale(1.02); }
  90% { transform: rotate(2deg) scale(1.01); }
  100% { transform: rotate(-2deg) scale(1.02); }
}
`;
if (typeof window !== 'undefined' && !document.head.querySelector('style[data-fido-wobble]')) {
  style.setAttribute('data-fido-wobble', 'true');
  document.head.appendChild(style);
} 

// Add pulse animation for the callout
const calloutPulseStyle = document.createElement('style');
calloutPulseStyle.innerHTML = `
@keyframes calloutPulse {
  0% { box-shadow: 0 4px 24px 0 rgba(255, 193, 7, 0.18); transform: scale(1); }
  50% { box-shadow: 0 8px 32px 0 rgba(255, 193, 7, 0.28); transform: scale(1.04); }
  100% { box-shadow: 0 4px 24px 0 rgba(255, 193, 7, 0.18); transform: scale(1); }
}
`;
if (typeof window !== 'undefined' && !document.head.querySelector('style[data-callout-pulse]')) {
  calloutPulseStyle.setAttribute('data-callout-pulse', 'true');
  document.head.appendChild(calloutPulseStyle);
} 